// SSE לשיחות (11 §1, 10 §1): fetch + ReadableStream במקום EventSource — כי EventSource לא שולח
// Authorization. אותה סמנטיקה: `Last-Event-ID` (שמור כאן, מתעדכן בכל אירוע) נשלח בכל התחברות מחדש,
// reconnect אוטומטי עם backoff, ניתוק מיידי ב-offline וחזרה מיידית ב-online, ו-watchdog: אם לא הגיע
// כלום (גם לא heartbeat) בזמן הקצוב — החיבור נחשב מת ונפתח מחדש. 4xx (403/404) — עצירה, לא לולאה.
import { ApiError, authed } from './api.ts';
import { createSseParser } from './sseParse.ts';
import { reportTrouble } from './health.ts';

export { createSseParser, type SseMessage } from './sseParse.ts';

export type SseState = 'connecting' | 'open' | 'reconnecting' | 'offline' | 'closed';

export type SseOptions = {
  lastEventId: string | null;
  onMessage: (m: import('./sseParse.ts').SseMessage) => void;
  onState?: (s: SseState) => void;
  onFatal?: (e: ApiError) => void;
  /** heartbeat בשרת כל 15s → 40s בלי כלום = חיבור מת */
  idleTimeoutMs?: number;
};

export function openSse(path: string, opts: SseOptions): { close: () => void; lastEventId: () => string | null } {
  let lastId = opts.lastEventId;
  let closed = false;
  let ctrl: AbortController | null = null;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let wake: (() => void) | null = null;
  const idleMs = opts.idleTimeoutMs ?? 40_000;
  const setState = (s: SseState) => opts.onState?.(s);

  const sleep = (ms: number) =>
    new Promise<void>((r) => {
      const t = setTimeout(() => {
        wake = null;
        r();
      }, ms);
      wake = () => {
        clearTimeout(t);
        wake = null;
        r();
      };
    });

  const onOffline = () => {
    setState('offline');
    ctrl?.abort();
  };
  const onOnline = () => {
    attempt = 0;
    wake?.();
  };
  window.addEventListener('offline', onOffline);
  window.addEventListener('online', onOnline);

  async function once(): Promise<'ended' | 'fatal'> {
    ctrl = new AbortController();
    const c = ctrl;
    const kick = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => c.abort(), idleMs);
    };
    kick();
    try {
      const headers: Record<string, string> = { accept: 'text/event-stream' };
      if (lastId) headers['last-event-id'] = lastId;
      const r = await authed(path, { headers, signal: c.signal, cache: 'no-store' });
      if (!r.body) return 'ended';
      setState('open');
      attempt = 0;
      const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
      const parse = createSseParser(
        (m) => {
          if (m.id) lastId = m.id;
          opts.onMessage(m);
        },
        // ה-api בניקוז (apps/api lib/drain.ts SSE_RECONNECT_HINT): עדכון מתוכנן — אם החיבור מחדש מתעכב, הגלולה תאמר זאת
        (c) => {
          if (c.includes('server is restarting')) reportTrouble('updating');
        },
      );
      for (;;) {
        const { value, done } = await reader.read();
        if (done) return 'ended';
        kick();
        parse(value);
      }
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        opts.onFatal?.(e);
        return 'fatal';
      }
      return 'ended';
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  void (async () => {
    setState('connecting');
    while (!closed) {
      if (!navigator.onLine) {
        setState('offline');
        await sleep(60_000);
        continue;
      }
      const res = await once();
      if (closed) break;
      if (res === 'fatal') {
        closed = true;
        break;
      }
      setState(navigator.onLine ? 'reconnecting' : 'offline');
      await sleep(Math.min(5000, 300 * 2 ** attempt++));
    }
    setState('closed');
  })();

  return {
    close: () => {
      closed = true;
      ctrl?.abort();
      wake?.();
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
    },
    lastEventId: () => lastId,
  };
}
